<script setup>
  import ComponentAccordion from "@components/Accordion/Accordion.vue";
  import ComponentAccordionItem from "@components/Accordion/AccordionItem.vue";
  import ComponentCategoryBadge from "@components/Badges/CategoryBadge.vue";
  import PatientinputAdditionalSymptoms from "@pages/Patientinput/Components/AdditionalSymptoms.vue";
  import PatientinputComponentStep from "@pages/Patientinput/Components/Step.vue";
  import {symptoms} from "@pages/Patientinput/Enums.js";
  import {MedCheckbox, MedFormField, MedLabel} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "PatientinputComponentStepSymptoms",
  });

  const props = defineProps({
    form: {
      type: Object,
      required: true,
    },
  });

  const categorySelected = (category) => {
    let count = 0;

    category.items.forEach((item) => {
      if (mivSelected(item.miv)) {
        count++;
      }
    });

    return count;
  };

  const mivSelected = (miv) => {
    return props.form.symptoms.mivs.some((iMiv) => {
      return Array.isArray(miv) ? miv.includes(iMiv) : iMiv === miv;
    });
  };

  const mivCheckboxChanged = (miv, selected) => {
    props.form.symptoms.mivs = props.form.symptoms.mivs.filter((iMiv) => {
      return Array.isArray(miv) ? !miv.includes(iMiv) : iMiv !== miv;
    });

    if (selected) {
      if (Array.isArray(miv)) {
        miv.forEach((m) => props.form.symptoms.mivs.push(m));
      } else {
        props.form.symptoms.mivs.push(miv);
      }
    }
  };
</script>

<template>
  <PatientinputComponentStep title="Probleme/Symptome" :test-id="getTestId()">
    <div class="flex flex-col gap-8">
      <section>
        <h2 class="sr-only">Häufig auftretende Probleme/Symptome</h2>
        <p>Bitte kreuzen Sie die Symptome an, die in den letzten vier Wochen verstärkt aufgetreten sind.</p>

        <ComponentAccordion>
          <ComponentAccordionItem
            v-for="(category, categoryIndex) in symptoms"
            :key="categoryIndex"
            :test-id="getTestId(`symptoms-step-${categoryIndex}`)"
          >
            <template v-if="categorySelected(category)" #title>
              <div class="flex items-center space-x-2">
                <h3>{{ category.title }}</h3>
                <sup>
                  <ComponentCategoryBadge color="info" :label="categorySelected(category).toString()" />
                </sup>
              </div>
            </template>
            <template v-else #title>
              <h3>{{ category.title }}</h3>
            </template>

            <div class="flex flex-col gap-3">
              <MedFormField
                v-for="(item, itemIndex) in category.items"
                :key="itemIndex"
                orientation="horizontal"
                size="mobile"
              >
                <MedCheckbox
                  variant="primary"
                  :model-value="mivSelected(item.miv)"
                  :data-test="getTestId(`symptoms-step-${categoryIndex}-category-item-${itemIndex}-checkbox`)"
                  @change="(event) => mivCheckboxChanged(item.miv, event)"
                />

                <MedLabel>
                  <span v-if="form.meta.is_patient">
                    {{ item.labelPatient }}
                  </span>

                  <span v-else>
                    {{ item.labelCarer }}
                  </span>
                </MedLabel>
              </MedFormField>
            </div>
          </ComponentAccordionItem>
        </ComponentAccordion>
      </section>

      <section>
        <h2 class="sr-only text-2xl">Weitere Probleme/Symptome</h2>

        <p v-if="form.meta.is_patient">
          Sie haben weitere Probleme/Symptome, die nicht in der Liste stehen? Dann können Sie danach suchen.
        </p>

        <p v-else>
          Ihr Patient hat weitere Probleme/Symptome, die nicht in der Liste stehen? Dann können Sie danach suchen.
        </p>

        <PatientinputAdditionalSymptoms :form="form" :test-id="getTestId('symptoms-step')" />
      </section>
    </div>
  </PatientinputComponentStep>
</template>

<style scoped>
  @reference "@css/app.css";

  section {
    @apply flex flex-col gap-3;
  }
</style>
